import { CommonModule } from '@angular/common';
import { Component, Input, inject } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	EmptyStateComponent,
	InfoGroupComponent,
	TagComponent,
	TileComponent,
} from '@tecalisdev/infinity';
import { DeveloperCurlButtonComponent } from '../../../shared/components/developer-curl-button/developer-curl-button.component';
import {
	CONNECTION_TYPE,
	EVALUATION_TYPE,
} from '../../../operations/models/operations.models';
import {
	buildFormChecksProcessDetail,
	hasFormChecksInProcess,
} from '../../../operations/utils/form-operation-detail-contract.util';
import {
	isOcrKoMatchDataError,
	listFormMatchCompareFields,
	shouldSuppressOcrKoInAntifraud,
} from '../../../operations/utils/form-operation-match-compare.util';
import { RolePermissionsService } from '../../../core/services/role-permissions.service';
import { ExpirationDatePipe } from '../../../core/pipes/expiration-date.pipe';
import { SafeDatePipe } from '../../../core/pipes/safe-date.pipe';
import { MasterService } from '../../../shared/services/master.service';
import { documentsCompareHasAnyTableRow } from '../../../shared/utils/documents-external-compare.util';
import { CHECK, IProcessDetailResponse } from '../../models/processes.models';
import { ProcessHelperService } from '../../services/process-helper.service';
import { ChecksComponent } from '../checks/checks.component';

/**
 * Paneles de comprobaciones (auth match, requisitos, contraste, antifraude).
 * Compartido entre detalle y revisión de proceso.
 */
@Component({
	selector: 'app-process-checks-panels',
	imports: [
		CommonModule,
		TranslatePipe,
		SafeDatePipe,
		ExpirationDatePipe,
		TileComponent,
		EmptyStateComponent,
		InfoGroupComponent,
		TagComponent,
		ChecksComponent,
		DeveloperCurlButtonComponent,
	],
	templateUrl: './process-checks-panels.component.html',
	styleUrl: './process-checks-panels.component.scss',
})
export class ProcessChecksPanelsComponent {
	@Input({ required: true }) detail!: IProcessDetailResponse;
	/** Muestra botones curl de desarrollador (solo detalle). */
	@Input() showDeveloperCurl = false;
	@Input() processUuid = '';

	public readonly CHECK = CHECK;
	public readonly processHelper = inject(ProcessHelperService);
	public readonly rolePermissions = inject(RolePermissionsService);
	public readonly masterService = inject(MasterService);
	private readonly trans = inject(TranslateService);

	protected readonly formMatchOnlyCompareChecks: CHECK[] = [CHECK.FORM_MATCH_DATA];
	protected readonly isOcrKoMatchDataError = isOcrKoMatchDataError;
	protected readonly formProcessRequirementChecks: CHECK[] = [
		CHECK.OCR_DOCUMENT_TYPE,
		CHECK.COUNTRY,
		CHECK.EXPIRATED,
		CHECK.LEGAL_AGE,
	];
	protected readonly formProcessCompareChecks: CHECK[] = [
		CHECK.FORM_MATCH_DATA,
		CHECK.AEAT,
		CHECK.AML,
		CHECK.PEP,
	];

	/** `true` si hay documentos con filas de contraste OCR vs registro. */
	public hasDocumentsExternalCompare(): boolean {
		const docsData = this.detail?.data?.DOCUMENTS;
		if (!docsData) {
			return false;
		}
		return documentsCompareHasAnyTableRow(docsData.document, docsData, this.trans);
	}

	/** Filas para mostrar datos enviados en POST /auth (`match_data`). */
	public authMatchDataEntries(): {
		key: string;
		label: string;
		value: string;
		isDate: boolean;
	}[] {
		const raw = this.detail?.general?.auth_match_data;
		if (!raw || typeof raw !== 'object') {
			return [];
		}
		return Object.entries(raw)
			.filter(([, v]) => this.isAuthMatchValuePresent(v))
			.map(([key, v]) => {
				const isDate = this.isAuthMatchDateField(key);
				const value = this.formatAuthMatchValue(v);
				return {
					key,
					label: this.authMatchFieldLabel(key),
					value,
					isDate,
				};
			});
	}

	private isAuthMatchDateField(key: string): boolean {
		return key === 'birthdate' || key === 'expiration_date';
	}

	private isAuthMatchValuePresent(v: unknown): boolean {
		if (v === null || v === undefined) {
			return false;
		}
		if (typeof v === 'string') {
			return v.trim() !== '';
		}
		return true;
	}

	private formatAuthMatchValue(v: unknown): string {
		if (v === null || v === undefined) {
			return '';
		}
		if (typeof v === 'object') {
			return JSON.stringify(v);
		}
		return String(v);
	}

	private authMatchFieldLabel(fieldKey: string): string {
		const map: Record<string, string> = {
			iban: 'PROCESSES.CREATE_MODAL.IBAN',
			nie: 'PROCESSES.CREATE_MODAL.NIE',
			nif: 'PROCESSES.CREATE_MODAL.NIF',
			identifier: 'PROCESSES.CREATE_MODAL.IDENTIFIER',
			cif: 'PROCESSES.CREATE_MODAL.SCRIPTURE',
			names: 'PROCESSES.CREATE_MODAL.NAMES',
			surnames: 'PROCESSES.CREATE_MODAL.SURNAMES',
			country: 'PROCESSES.CREATE_MODAL.COUNTRY',
			birthdate: 'PROCESSES.CREATE_MODAL.BIRTHDATE',
			expiration_date: 'PROCESSES.CREATE_MODAL.EXPIRATION_DATE',
			support_number: 'PROCESSES.CREATE_MODAL.SUPPORT_NUMBER',
			id_document_type: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ID_DOCUMENT_TYPE',
			gender: 'PROCESSES.DETAIL.GENDER',
			nationality: 'PROCESSES.DETAIL.NACIONALITY',
			address: 'PROCESSES.DETAIL.ADDRESS',
			address_number: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ADDRESS_NUMBER',
			address_floor: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ADDRESS_FLOOR',
			address_door: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ADDRESS_DOOR',
			municipality: 'PROCESSES.DETAIL.CITY',
			postal_code: 'PROCESSES.DETAIL.AUTH_MATCH_FIELD.postal_code',
			province: 'PROCESSES.DETAIL.PROVINCE',
		};
		const i18nKey = map[fieldKey] ?? `PROCESSES.DETAIL.AUTH_MATCH_FIELD.${fieldKey}`;
		const translated = this.trans.instant(i18nKey);
		return translated !== i18nKey ? translated : fieldKey;
	}

	public get hasFormChecks(): boolean {
		return hasFormChecksInProcess(this.detail);
	}

	/**
	 * Subsección FORM en comprobaciones: solo sin operación OCR nativa.
	 */
	public get showFormChecksInProcess(): boolean {
		return this.hasFormChecks && !this.detail?.data?.OCR;
	}

	/** Cotejo FORM en Contraste aunque exista operación OCR nativa. */
	public get showFormMatchCompareInProcess(): boolean {
		return listFormMatchCompareFields(this.detail?.data?.FORM).length > 0;
	}

	public get shouldShowOcrKoInAntifraud(): boolean {
		const ocr = this.detail?.data?.OCR;
		if (!ocr || !this.processHelper.shouldShowOperationKoReason(ocr)) {
			return false;
		}
		return !shouldSuppressOcrKoInAntifraud(ocr, this.detail?.data?.FORM);
	}

	public get shouldShowPolKoInAntifraud(): boolean {
		return this.processHelper.shouldShowOperationKoReason(this.detail?.data?.POL);
	}

	public get ocrProcessCompareChecks(): CHECK[] {
		const checks: CHECK[] = [CHECK.READ_MRZ, CHECK.AEAT, CHECK.AML, CHECK.PEP];
		if (isOcrKoMatchDataError(this.detail?.data?.OCR) && !this.showFormMatchCompareInProcess) {
			checks.push(CHECK.KO_OCR);
		}
		return checks;
	}

	public get hasFormProcessCompareChecks(): boolean {
		const form = this.detail?.data?.FORM;
		if (form && listFormMatchCompareFields(form).length) {
			return true;
		}
		const ocr = this.formChecksProcessDetail?.data?.OCR;
		if (!ocr?.antifraud) {
			return false;
		}
		const a = ocr.antifraud;
		return (
			this.processHelper.shouldDisplayAntifraudCheck(ocr.state, a.AEAT) ||
			this.processHelper.shouldDisplayAntifraudCheck(ocr.state, a.AML) ||
			this.processHelper.shouldDisplayAntifraudCheck(ocr.state, a.PEP)
		);
	}

	/** Proceso con `data.OCR` proyectado desde `data.FORM.checks.ocr`. */
	public get formChecksProcessDetail(): IProcessDetailResponse | null {
		const snapshot = buildFormChecksProcessDetail(this.detail);
		const ocr = snapshot?.data?.OCR;
		if (ocr) {
			this.processHelper.setYearsOld(ocr);
		}
		return snapshot;
	}

	public get hasProcessCompareChecks(): boolean {
		return (
			this.processHelper.hasDisplayableOcrCompareAntifraud(this.detail?.data?.OCR) ||
			isOcrKoMatchDataError(this.detail?.data?.OCR) ||
			(this.showFormChecksInProcess && this.hasFormProcessCompareChecks) ||
			(this.showFormMatchCompareInProcess && !!this.formChecksProcessDetail) ||
			this.processHelper.shouldDisplayAntifraudCheck(
				this.detail?.data?.POL?.state,
				this.detail?.data?.POL?.antifraud?.FACIAL_RECOGNITION,
			) ||
			this.detail?.data?.EVALUATION?.evaluation === EVALUATION_TYPE.MATCH_DATA ||
			this.hasDocumentsExternalCompare()
		);
	}

	public get hasProcessAntifraudChecks(): boolean {
		const ocr = this.detail?.data?.OCR?.antifraud;
		const pol = this.detail?.data?.POL?.antifraud;
		const showImageFraud = this.processHelper.canShowOcrImageFraudChecks();
		return !!(
			ocr?.PROHIBITED_CAMERAS ||
			pol?.PROHIBITED_CAMERAS ||
			(showImageFraud && ocr?.PHOTOCOPY) ||
			(showImageFraud && ocr?.MANIPULATED_PHOTO) ||
			(showImageFraud && ocr?.SCREENPHOTO) ||
			this.detail?.locks?.blocked_identifiers?.length ||
			this.detail?.locks?.blocked_faces?.length ||
			pol?.FRAUD_FACE ||
			pol?.ANTISPOOF ||
			(this.rolePermissions.isSuperadmin() && pol?.ANTISPOOF_LAB) ||
			this.shouldShowOcrKoInAntifraud ||
			this.shouldShowPolKoInAntifraud
		);
	}

	public get hasProcessRequirementsChecks(): boolean {
		return !!(
			this.detail?.data?.OCR ||
			this.detail?.data?.DOCUMENTS ||
			this.hasEvaluationRequirementsChecks ||
			this.hasAttorneyPowersRequirementsChecks ||
			this.showFormChecksInProcess
		);
	}

	/** EVALUATION en requisitos solo si no es poderes (SCRIPTURE); ese bloque quedaría vacío. */
	public get hasEvaluationRequirementsChecks(): boolean {
		const evaluation = this.detail?.data?.EVALUATION;
		return !!evaluation && evaluation.type?.toUpperCase() !== CONNECTION_TYPE.SCRIPTURE;
	}

	/** Poderes / BORME: check de estado en Requisitos (NIF-800). */
	public get hasAttorneyPowersRequirementsChecks(): boolean {
		return !!this.detail?.data?.ATTORNEY_POWERS;
	}

	public get showChecksTabEmptyState(): boolean {
		const hasContent =
			this.hasProcessRequirementsChecks ||
			this.hasProcessCompareChecks ||
			this.hasProcessAntifraudChecks ||
			this.showFormChecksInProcess ||
			this.showFormMatchCompareInProcess;
		return !hasContent && this.authMatchDataEntries().length === 0;
	}
}
